/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { ResizeSensor } from '@blueprintjs/core';
import React, { useEffect, useState } from 'react';
import { useFetchEndpointData } from 'hooks/useFetchEndpointData';
import { WidgetContainer } from '/views/common/widgetContainer';
import { WIDGETS } from '/widgets';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { useFilteredProperties } from 'utilities/srsUtils';
import { ErrorBoundary } from 'components/errorBoundary';
import { WidgetControls } from 'views/common/WidgetControls';

/**
 * A ExplorerCard provides a generalized method for rendering a chart onto a
 * Explorer. The Card is responsible for rendering a ChartContainer,
 * which in turn will instantiate and render the specific Chart.
 *
 * @returns {*}
 * @constructor
 */
export const ExplorerCard = ({ result, selectedDefinitionName, dispatch, card, cardIndex, stopResize = false }) => {
	// Store dimensions of things
	const [dimensions, setDimensions] = useState({ width: 300, height: 400 });

	// properties based on type
	// These values are read/pulled from the widget config.ts file
	const { Actions, flags, types, createDefaultOptions, createDefaultConfiguration, allowedProperties = [] } = WIDGETS[
		card.type
	];

	// filter the properties based on flags, types, and/or allowedProperties
	const filteredProperties = useFilteredProperties(selectedDefinitionName, flags, types, allowedProperties);

	// provide set of default endpoint options if none given
	useEffect(() => {
		if (!card?.endpointQuery?.options) {
			const options = createDefaultOptions(filteredProperties);
			dispatch(WorkspaceActionCreator.options(cardIndex, options));
		}
	}, [card, selectedDefinitionName, dispatch, filteredProperties, cardIndex, createDefaultOptions]);

	// provide a default widget configuration if none given
	useEffect(() => {
		// Where dynamic configurations come in (i.e. cyhy/src/configurations/widgetConfigurations.json
		// TODO make createDefaultConfiguration required for all charts
		if (!card?.widgetConfiguration && createDefaultConfiguration) {
			const widgetConfig = createDefaultConfiguration(filteredProperties);
			dispatch(WorkspaceActionCreator.configuration(cardIndex, widgetConfig));
		}
	}, [card, cardIndex, createDefaultConfiguration, dispatch, filteredProperties]);

	// resize the widgets appropriately
	function onResize(entries) {
		if (!stopResize) {
			const entry = entries[0].contentRect;
			setDimensions({ width: entry.width, height: entry.height });
		}
	}

	return (
		<div css={cardCss}>

			{Actions && (
				<div css={headerCss}>
					<Actions cardIndex={cardIndex} actions={card.actions} />
				</div>
			)}

			{/* used for catching endpoint errors */}
			<ErrorBoundary
				description={
					'An error occurred while fetching data for this widget. Adjust the parameter(s) and try again or refresh the page.'
				}
				retryButtonText={'Fetch again'}
			>
				<ResizeSensor onResize={onResize}>
					<WidgetControls>
						<div cy-test="widget-container" css={containerCss}>
							<ExplorerData
								cardIndex={cardIndex}
								dimensions={dimensions}
								card={card}
								result={result}
								selectedDefinitionName={selectedDefinitionName}
							/>
						</div>
					</WidgetControls>
				</ResizeSensor>
			</ErrorBoundary>
		</div>
	);
};

function ExplorerData({ cardIndex, dimensions, card, result, selectedDefinitionName }) {
	// fetch the filtered data
	const { data, loading } = useFetchEndpointData(result, selectedDefinitionName, card.type, card.endpointQuery);

	// Choose when data refreshes
	const [validData, setValidData] = useState(null);
	useEffect(() => {
		if (data || result?.resultId === 0) {
			setValidData(data);
		}
	}, [data, result]);

	return (
		<WidgetContainer
			cy-test="widget-container"
			data={validData}
			cardIndex={cardIndex}
			type={card.type}
			loading={loading}
			dimensions={dimensions}
			actions={card.actions}
			selectedDefinitionName={selectedDefinitionName}
			widgetConfiguration={card.widgetConfiguration}
			endpointQuery={card.endpointQuery}
		/>
	);
}

const headerCss = css`
	flex: 0 0 auto;
	display: flex;
`;

const cardCss = css`
	flex: 1 1 auto;
	display: flex;
	flex-flow: column;
	overflow: hidden;
`;

const containerCss = css`
	flex: 1 1 auto;
	overflow: hidden;
	position: relative;
	display: flex;
`;
